import { Button, Form } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";
import { SourceFilesListProps } from "./SourceFilesList";

/** Handle the logic for sorting the files list and deciding which radio button is checked. */
function useHandleSortingFilesList(props: SourceFilesListProps) {
    const { sortedByName, isSortedByName, sortedByDate, isSortedByDate } = useMemo(() => {
        const sortedByName = [...props.sourceFiles].sort((a, b) =>
            a.file.name.localeCompare(b.file.name, undefined, { numeric: true, sensitivity: "base" }),
        );

        const allHaveStart = props.sourceFiles.every((f) => f.start !== undefined);
        const sortedByDate = allHaveStart
            ? [...props.sourceFiles].sort((a, b) => {
                  return a.start.valueOf() - b.start.valueOf();
              })
            : undefined;

        return {
            sortedByName,
            isSortedByName: props.sourceFiles.every((a, i) => a.file.name == sortedByName[i].file.name),
            sortedByDate,
            isSortedByDate:
                sortedByDate !== undefined &&
                props.sourceFiles.every((a, i) => a.file.name == sortedByDate[i].file.name),
        };
    }, [props.sourceFiles]);

    const defaultChecked = isSortedByName ? "name" : isSortedByDate ? "date" : null;
    // Value used to alternate between checking the date or name radios, even when sorting by either
    // leads to the same result
    const [checked, setChecked] = useState<"name" | "date" | null>(defaultChecked);

    useEffect(() => {
        // Skip reset if checked value is valid
        if ((isSortedByDate && checked == "date") || (isSortedByName && checked == "name")) {
            return;
        }
        setChecked(defaultChecked);
    }, [props.sourceFiles]);

    function sortByName() {
        setChecked("name");
        props.resetSelectedFiles();

        if (!isSortedByName) {
            props.setSourceFiles(sortedByName);
        }
    }

    function sortByDate() {
        if (sortedByDate === undefined) {
            return;
        }

        setChecked("date");
        props.resetSelectedFiles();

        if (!isSortedByDate) {
            props.setSourceFiles(sortedByDate);
        }
    }

    return { checked, sortByName, sortByDate };
}

function SourceFilesListControls(props: SourceFilesListProps) {
    const { checked, sortByName, sortByDate } = useHandleSortingFilesList(props);

    function clear() {
        props.resetSelectedFiles();
        props.resetSourceFiles();
    }

    return (
        <div className="gap-4 d-flex justify-content-between align-items-center">
            <div></div>

            <div className="gap-3 d-flex">
                <Form.Label className="gap-2 m-0 d-flex align-items-center">
                    <Form.Check
                        inline
                        className="m-0"
                        checked={checked == "name"}
                        name="sort"
                        type="radio"
                        onChange={sortByName}
                        title="Sort the source files alphabetically by their name"
                    />
                    Sort by name
                </Form.Label>

                <Form.Label className="gap-2 m-0 d-flex align-items-center">
                    <Form.Check
                        disabled={sortByDate === undefined}
                        inline
                        className="m-0"
                        checked={checked == "date"}
                        name="sort"
                        type="radio"
                        onChange={sortByDate}
                        title="Sort the source files by each file's last modification date"
                    />
                    Sort by date
                </Form.Label>
            </div>

            <div>
                <Button variant="outline-danger" onClick={clear} title="Warning: Clear source files list">
                    Clear
                </Button>
            </div>
        </div>
    );
}

export default SourceFilesListControls;
